import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiStar, FiTrendingUp, FiBookOpen, FiUsers } from 'react-icons/fi';

const Superscript = styled.sup`
  font-size: 0.5em;
  font-weight: 700;
  vertical-align: 0.3em;
  line-height: 0;
  margin-left: -2px;
  color: inherit;
  position: relative;
  top: -0.1em;
`;

const HomeContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 120px 0 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 40px;
  opacity: 0.9;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const HeroButton = styled(Link)`
  display: inline-block;
  padding: 16px 32px;
  background-color: #2563eb;
  color: white;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    background-color: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
  }
`;

const HeroButtonSecondary = styled(Link)`
  display: inline-block;
  padding: 16px 32px;
  background-color: transparent;
  color: white;
  font-weight: 600;
  border: 2px solid white;
  border-radius: 8px;
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    background-color: white;
    color: #667eea;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 0;
  background-color: #f8fafc;
`;

const FeaturesContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1f2937;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 40px 30px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1f2937;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(135deg, #1f2937, #374151);
  color: white;
`;

const StatsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  text-align: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const StatItem = styled.div`
  h3 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 8px;
    color: #60a5fa;
  }
  
  p {
    font-size: 1.125rem;
    opacity: 0.9;
  }
`;

const CTA = styled.section`
  padding: 80px 0;
  background-color: #f8fafc;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #1f2937;
`;

const CTASubtitle = styled.div`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  
  p {
    font-size: 1.25rem;
    color: #4b5563;
    margin: 0;
    line-height: 1.7;
    font-weight: 400;
  }
`;

const CTALogoImage = styled.img`
  height: 36px;
  width: auto;
  object-fit: contain;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const HomePage = ({ onAddToCart }) => {
  const featuredBooks = [
    {
      id: 1,
      title: "The Great Gatsby",
      author: "F. Scott Fitzgerald",
      price: 9.99,
      image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=400&fit=crop",
      rating: 4.5
    },
    {
      id: 2,
      title: "To Kill a Mockingbird",
      author: "Harper Lee",
      price: 11.99,
      image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=300&h=400&fit=crop",
      rating: 4.8
    },
    {
      id: 3,
      title: "1984",
      author: "George Orwell",
      price: 10.99,
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=400&fit=crop",
      rating: 4.6
    }
  ];

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>Discover how multimedia transforms storytelling</HeroTitle>
            <HeroSubtitle>
              where your creative artistry meets interactive innovation. Join our community of authors and bring your eBook to life with immersive, multimedia features.
            </HeroSubtitle>
            <HeroButtons>
              {/* <HeroButton to="/books">Browse Books</HeroButton> */}
              <HeroButtonSecondary to="/register">Join as Author</HeroButtonSecondary>
            </HeroButtons>
          </motion.div>
        </HeroContent>
      </HeroSection>

      {/* <FeaturesSection>
        <FeaturesContent>
          <SectionTitle>Why Choose M<Superscript>2</Superscript>eBooks.com?</SectionTitle>
          <SectionSubtitle>
            We're committed to providing the best digital reading experience
          </SectionSubtitle>
          <FeaturesGrid>
            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FeatureIcon>
                <FiBookOpen size={32} />
              </FeatureIcon>
              <FeatureTitle>Massive Library</FeatureTitle>
              <FeatureDescription>
                Access over 5 million books from thousands of publishers worldwide
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <FeatureIcon>
                <FiStar size={32} />
              </FeatureIcon>
              <FeatureTitle>Curated Selection</FeatureTitle>
              <FeatureDescription>
                Handpicked recommendations from our expert team of book lovers
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <FeatureIcon>
                <FiTrendingUp size={32} />
              </FeatureIcon>
              <FeatureTitle>Best Prices</FeatureTitle>
              <FeatureDescription>
                Competitive pricing with regular deals and discounts on popular titles
              </FeatureDescription>
            </FeatureCard>
            
            <FeatureCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <FeatureIcon>
                <FiUsers size={32} />
              </FeatureIcon>
              <FeatureTitle>Community</FeatureTitle>
              <FeatureDescription>
                Join millions of readers sharing reviews and recommendations
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </FeaturesContent>
      </FeaturesSection> */}

      <StatsSection>
        <StatsContent>
          <SectionTitle style={{ color: 'white' }}>Turning Your eBook Dreams Into Reality</SectionTitle>
          <SectionSubtitle style={{ color: '#d1d5db' }}>
            Join our fast-growing community of multimedia eBook enthusiasts.
          </SectionSubtitle>
          <StatsGrid>
            <StatItem>
              <h3>Together Reading</h3>
              <p>Share immersive storytelling that brings people closer through innovation.</p>
            </StatItem>
            <StatItem>
              <h3>Connected Reading</h3>
              <p>Discover a world of endless stories, knowledge, and creativity—crafted for every passion.</p>
            </StatItem>
            {/* <StatItem>
              <h3>50K+</h3>
              <p>New Books Monthly</p>
            </StatItem>
            <StatItem>
              <h3>99%</h3>
              <p>Customer Satisfaction</p>
            </StatItem> */}
          </StatsGrid>
        </StatsContent>
      </StatsSection>

      <CTA>
        <CTAContent>
          <CTATitle>Ready to Start Creating?</CTATitle>
          <CTASubtitle>
            <p>Multi-mediatize your eBook and elevate your story with interactive, immersive features.</p>
          
          </CTASubtitle>
          <CTAButtons>
            <HeroButton to="/register">Get Started</HeroButton>
            {/* <HeroButton to="/books">Browse Books</HeroButton> */}
          </CTAButtons>
        </CTAContent>
      </CTA>
    </HomeContainer>
  );
};

export default HomePage;
